import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";

const muted = "var(--text-disabled)";

/**
 * InvestigationTrail — what Ask is doing while it answers, and what it did.
 *
 * One line, not a card. While it works the line says what it is trying to
 * find out right now — the model's own purpose for the call ("failed runs by
 * agent · last 7 days"), not the tool it called — and changes as it moves on.
 * When the answer lands it folds to "Worked for 14s · 6 steps". Either way it
 * opens into the list of what was looked at and what came back.
 */
export default function InvestigationTrail({ inv }) {
  const tools = Array.isArray(inv?.tools) ? inv.tools : [];
  const calls = Array.isArray(inv?.calls) ? inv.calls : [];
  const toolResults = Array.isArray(inv?.toolResults) ? inv.toolResults : [];
  const reasoning = Array.isArray(inv?.reasoning) ? inv.reasoning : [];
  const steps = Array.isArray(inv?.steps) ? inv.steps : [];
  const streaming = !!inv?.streaming;
  const hasSteps = steps.length > 0;
  const hasAnyTrail = hasSteps || tools.length > 0 || toolResults.length > 0 || reasoning.length > 0;
  const [open, setOpen] = useState(false);
  // Fold when the answer arrives, the way a finished thought tucks away.
  const wasStreaming = useRef(streaming);
  useEffect(() => {
    if (wasStreaming.current && !streaming) setOpen(false);
    wasStreaming.current = streaming;
  }, [streaming]);

  if (!inv) return null;
  if (!hasAnyTrail && !streaming) return null;

  const rows = trailRows({ calls, toolResults, streaming });
  const stepCount = hasSteps ? steps.filter((x) => x.phase === "node").length : toolResults.length;
  const label = streaming ? liveLine(inv, rows) : doneLine(inv, stepCount);
  const canOpen = rows.length > 0 || hasSteps || reasoning.length > 0;

  return (
    <div className="w-full min-w-0 mb-2 text-[13px]">
      <button
        type="button"
        onClick={() => canOpen && setOpen((v) => !v)}
        className="inline-flex max-w-full items-center gap-1.5 text-left select-none"
        style={{ color: theme.textSecondary, cursor: canOpen ? "pointer" : "default" }}
        aria-expanded={open}
      >
        {streaming && <Loader2 size={13} className="animate-spin flex-shrink-0" aria-hidden style={{ color: "rgb(var(--trodo-primary-rgb))" }} />}
        <span className="truncate" aria-live={streaming ? "polite" : undefined}>{label}</span>
        {canOpen && (open
          ? <ChevronDown size={14} className="flex-shrink-0" aria-hidden />
          : <ChevronRight size={14} className="flex-shrink-0" aria-hidden />)}
      </button>

      {open && (
        <div className="mt-2 ml-1.5 pl-3 space-y-1.5 text-xs" style={{ borderLeft: `1px solid ${theme.border}` }}>
          {hasSteps && <StepLog steps={steps} plan={inv.plan} streaming={streaming} />}
          {groupByWave(rows).map((group, gi) => (
            <div key={`g-${gi}`} className="space-y-1.5">
              {group.wave != null && hasManyWaves(rows) && (
                <div style={{ fontSize: 11, color: muted }}>Wave {group.wave}</div>
              )}
              {group.rows.map((r, i) => <TrailRow key={`${gi}-${i}`} r={r} />)}
            </div>
          ))}
          {reasoning.map((r, i) => (
            r && r.reason ? (
              <div key={`r-${i}`} className="italic whitespace-pre-wrap" style={{ color: theme.textSecondary }}>{r.reason}</div>
            ) : null
          ))}
        </div>
      )}
    </div>
  );
}

/** "asked  →  got" → its two halves; anything without the arrow is all question. */
function splitDetail(detail) {
  const d = String(detail || "");
  const i = d.indexOf("→");
  if (i < 0) return { asked: d.trim(), got: "" };
  return { asked: d.slice(0, i).trim(), got: d.slice(i + 1).trim() };
}

/**
 * The goal as a person would say it. Engine ids ("total_runs", "ds_7") read as
 * plumbing: underscores become spaces, dataset handles leave the goal (they
 * stay in what came back), and it starts with a capital.
 */
function humanGoal(text) {
  const t = String(text || "")
    .replace(/(^|\s|·)ds_\d+(?=\s|·|$)/g, "$1")
    .replace(/([a-z0-9])_(?=[a-z0-9])/gi, "$1 ")
    .replace(/(\s*·\s*)+/g, " · ")
    .replace(/^\s*·\s*|\s*·\s*$/g, "")
    .trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

/** Finished calls in order, then the ones still running (calls not yet answered). */
function trailRows({ calls, toolResults, streaming }) {
  const done = toolResults.map((tr) => {
    const { asked, got } = splitDetail(tr.detail);
    return { state: tr.status === "error" ? "error" : "done", goal: humanGoal(asked) || tr.tool || tr.name || "Step", tool: tr.tool, got, note: tr.error || tr.summary || "", status: tr.status, wave: tr.wave };
  });
  if (!streaming) return done;
  const running = calls.slice(toolResults.length).map((c) => ({
    state: "running", goal: humanGoal(splitDetail(c.detail).asked) || c.tool || "Working", tool: c.tool, got: "", note: "", wave: c.wave,
  }));
  return [...done, ...running];
}

function liveLine(inv, rows) {
  // Stop was pressed; the run ends at its next check, which can be seconds away.
  if (inv.stopping) return "Stopping…";
  const current = [...rows].reverse().find((r) => r.state === "running");
  if (current) return sentence(current.goal);
  return sentence((inv.thinkingLabel || "Thinking").replace(/[….]+$/, ""));
}

function doneLine(inv, stepCount) {
  const ms = typeof inv.durationMs === "number" ? inv.durationMs
    : inv.startedAt && inv.endedAt ? inv.endedAt - inv.startedAt : null;
  const took = ms != null ? `Worked for ${formatDuration(ms)}` : "Worked";
  return stepCount > 0 ? `${took} · ${stepCount} step${stepCount === 1 ? "" : "s"}` : took;
}

function sentence(text) {
  const t = String(text || "").trim();
  return t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}…` : "Working…";
}

function formatDuration(ms) {
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
}

function hasManyWaves(rows) {
  return new Set(rows.map((r) => r.wave).filter((w) => w != null)).size > 1;
}

/**
 * A step that failed, in the reader's words. Most are Lucid's own wrong turns
 * that it then corrects — an aggregate that cannot take a filter, a column
 * that does not exist — and printing the engine's sentence in red read as the
 * answer failing. The raw text stays on hover.
 */
export function friendlyStepError(raw) {
  const t = String(raw || "");
  if (/time(d)?\s?out|took too long|deadline/i.test(t)) return "Took too long; Lucid went another way.";
  if (/rate.?limit|429|overloaded|unavailable/i.test(t)) return "The model provider was busy; Lucid retried or went another way.";
  return "Didn't fit this question; Lucid went another way.";
}

function TrailRow({ r }) {
  const icon = r.state === "running"
    ? <Loader2 size={12} className="animate-spin" style={{ color: "rgb(var(--trodo-primary-rgb))" }} />
    : r.state === "error"
      ? <X size={12} style={{ color: muted }} />
      : <Check size={12} style={{ color: muted }} />;
  const meta = [r.tool && r.tool !== r.goal ? r.tool : null, r.got || null].filter(Boolean).join(" → ");
  return (
    <div className="flex items-start gap-2 min-w-0">
      <span className="mt-[2px] flex-shrink-0" aria-label={r.state}>{icon}</span>
      <span className="min-w-0">
        <span style={{ color: theme.textPrimary }}>{r.goal}</span>
        {meta && <span style={{ color: muted }}> · {meta}</span>}
        {r.note && (r.state === "error"
          ? <div title={r.note} style={{ color: muted, fontSize: 11 }}>{friendlyStepError(r.note)}</div>
          : <div className="whitespace-pre-wrap" style={{ color: muted, fontSize: 11 }}>{r.note}</div>)}
      </span>
    </div>
  );
}

/* ── v2 persistent step log ──────────────────────────────────────────────── */

function StepStatusIcon({ status }) {
  if (status === "ok") {
    return (
      <span aria-label="done" style={{ color: "rgb(16,185,129)", fontSize: 11, width: 14, textAlign: "center", flexShrink: 0 }}>✓</span>
    );
  }
  if (status === "error") {
    return (
      <span aria-label="failed" style={{ color: "rgb(239,68,68)", fontSize: 11, width: 14, textAlign: "center", flexShrink: 0 }}>✕</span>
    );
  }
  if (status === "skipped") {
    return (
      <span aria-label="skipped" style={{ color: "rgb(245,158,11)", fontSize: 11, width: 14, textAlign: "center", flexShrink: 0 }}>⤳</span>
    );
  }
  if (status === "running") {
    return <Loader2 size={12} aria-label="running" className="animate-spin flex-shrink-0" style={{ margin: 1, color: "rgb(var(--trodo-primary-rgb))" }} />;
  }
  // pending
  return (
    <span aria-label="pending" style={{ display: "inline-block", width: 6, height: 6, margin: 4, borderRadius: 99, background: muted, opacity: 0.45, flexShrink: 0 }} />
  );
}

const KIND_COLORS = {
  sql: "rgb(59,130,246)",
  python: "rgb(168,85,247)",
};

function StepRow({ s }) {
  const kind = s.kind || null;
  const kindColor = kind ? (KIND_COLORS[kind] || (String(kind).startsWith("action") ? "rgb(245,158,11)" : "rgb(20,184,166)")) : null;
  const metaBits = [];
  if (typeof s.rows === "number") metaBits.push(`${s.rows.toLocaleString()} rows`);
  if (s.cached) metaBits.push("cached");
  if (typeof s.ms === "number" && !s.cached) metaBits.push(s.ms >= 1000 ? `${(s.ms / 1000).toFixed(1)}s` : `${s.ms}ms`);
  return (
    <div className="flex items-start gap-2 py-[3px]" style={{ color: theme.textPrimary }}>
      <span style={{ marginTop: 1 }}><StepStatusIcon status={s.status} /></span>
      <span className="min-w-0 flex-1">
        <span style={{ fontSize: 12, color: s.status === "pending" ? muted : theme.textPrimary }}>
          {s.label || s.id}
        </span>
        {s.detail && (
          <span style={{ fontSize: 11, color: muted }}> — {s.detail}</span>
        )}
        {s.error && (
          <div title={s.error} style={{ fontSize: 11, color: muted, marginTop: 1 }}>{friendlyStepError(s.error)}</div>
        )}
      </span>
      <span className="flex items-center gap-1.5 flex-shrink-0" style={{ marginTop: 1 }}>
        {metaBits.length > 0 && (
          <span style={{ fontSize: 11, color: muted, fontVariantNumeric: "tabular-nums" }}>
            {metaBits.join(" · ")}
          </span>
        )}
        {kind && (
          <code
            style={{
              fontSize: 10, padding: "1px 5px", borderRadius: 5,
              border: `1px solid ${kindColor}33`, color: kindColor,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {kind}
          </code>
        )}
      </span>
    </div>
  );
}

function StepLog({ steps, plan, streaming }) {
  // Stable display order: intake → plan(assumptions) → nodes (plan order) → compile → narrative
  const byPhase = (p) => steps.filter((s) => s.phase === p);
  const nodeOrder = plan?.nodes?.map((n) => n.id) || null;
  const nodes = byPhase("node").slice();
  if (nodeOrder) nodes.sort((a, b) => nodeOrder.indexOf(a.id) - nodeOrder.indexOf(b.id));
  const ordered = [...byPhase("intake"), ...byPhase("compile"), ...nodes, ...byPhase("narrative")];
  const assumptions = plan?.assumptions || [];

  return (
    <div>
      <div className="divide-y" style={{ borderColor: theme.border }}>
        {ordered.map((s) => (
          <StepRow key={`${s.phase}:${s.id}`} s={s} />
        ))}
      </div>
      {assumptions.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t" style={{ borderColor: theme.border }}>
          <span style={{ fontSize: 11, color: muted }}>
            Assumptions: {assumptions.join("; ")}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * A deep run is phases → waves → investigators, and every row used to land in
 * one flat list: an investigator's objective, then its tool calls, then a
 * sibling's, with nothing to say which belonged to which. Consecutive rows
 * from the same wave are gathered under it; quick runs carry no wave and are
 * untouched.
 */
function groupByWave(rows) {
  const out = [];
  for (const tr of rows) {
    const wave = typeof tr?.wave === 'number' ? tr.wave : null;
    const last = out[out.length - 1];
    if (last && last.wave === wave) last.rows.push(tr);
    else out.push({ wave, rows: [tr] });
  }
  return out;
}
